import { useState, useEffect } from "react";
import {
  FiCreditCard,
  FiFileText,
  FiAlertTriangle,
  FiCheckCircle,
  FiChevronLeft,
  FiChevronRight,
  FiInbox,
  FiEye,
  FiDownload,
} from "react-icons/fi";

/* ============================================
   Style Constants
   ============================================ */
const CARD_STYLE = {
  background: "rgba(15, 23, 42, 0.5)",
  border: "1px solid rgba(51, 65, 85, 0.3)",
  backdropFilter: "blur(12px)",
};

const CARD_HOVER_STYLE = {
  borderColor: "rgba(0, 180, 216, 0.15)",
  boxShadow: "0 8px 30px rgba(0, 0, 0, 0.2)",
};

const STATUS_THEMES = {
  Lunas: {
    bg: "rgba(52, 211, 153, 0.1)",
    border: "1px solid rgba(52, 211, 153, 0.2)",
    color: "#34d399",
    icon: FiCheckCircle,
  },
  Terlambat: {
    bg: "rgba(251, 146, 60, 0.1)",
    border: "1px solid rgba(251, 146, 60, 0.2)",
    color: "#fb923c",
    icon: FiAlertTriangle,
  },
  Pending: {
    bg: "rgba(250, 204, 21, 0.1)",
    border: "1px solid rgba(250, 204, 21, 0.2)",
    color: "#facc15",
    icon: FiAlertTriangle,
  },
  Gagal: {
    bg: "rgba(239, 68, 68, 0.1)",
    border: "1px solid rgba(239, 68, 68, 0.2)",
    color: "#f87171",
    icon: FiAlertTriangle,
  },
  "Diproses": {
    bg: "rgba(96, 165, 250, 0.1)",
    border: "1px solid rgba(96, 165, 250, 0.2)",
    color: "#60a5fa",
    icon: FiAlertTriangle,
  },
  "Selesai": {
    bg: "rgba(52, 211, 153, 0.1)",
    border: "1px solid rgba(52, 211, 153, 0.2)",
    color: "#34d399",
    icon: FiCheckCircle,
  },
  "Ditolak": {
    bg: "rgba(239, 68, 68, 0.1)",
    border: "1px solid rgba(239, 68, 68, 0.2)",
    color: "#f87171",
    icon: FiAlertTriangle,
  },
  default: {
    bg: "rgba(100, 116, 139, 0.1)",
    border: "1px solid rgba(100, 116, 139, 0.2)",
    color: "#94a3b8",
    icon: FiAlertTriangle,
  },
};

/* ============================================
   Tab Config
   ============================================ */
const TABS = [
  {
    key: "payment",
    label: "Riwayat Pembayaran",
    icon: FiCreditCard,
    color: "#a78bfa",
    bg: "rgba(167, 139, 250, 0.12)",
    borderActive: "rgba(167, 139, 250, 0.3)",
  },
  {
    key: "invoice",
    label: "Data Invoice",
    icon: FiFileText,
    color: "#22d3ee",
    bg: "rgba(0, 180, 216, 0.12)",
    borderActive: "rgba(0, 180, 216, 0.3)",
  },
  {
    key: "ticket",
    label: "Tiket Gangguan",
    icon: FiAlertTriangle,
    color: "#fb923c",
    bg: "rgba(251, 146, 60, 0.12)",
    borderActive: "rgba(251, 146, 60, 0.3)",
  },
];

/* ============================================
   Table Headers per Tab
   ============================================ */
const HEADERS = {
  payment: [
    { label: "No" },
    { label: "Periode" },
    { label: "Tanggal Bayar" },
    { label: "Metode" },
    { label: "Jumlah", align: "right" },
    { label: "Status" },
    { label: "Aksi", align: "right" },
  ],
  invoice: [
    { label: "No" },
    { label: "No Invoice" },
    { label: "Periode" },
    { label: "Jumlah", align: "right" },
    { label: "Status" },
    { label: "Aksi", align: "right" },
  ],
  ticket: [
    { label: "No" },
    { label: "No Tiket" },
    { label: "Keluhan" },
    { label: "Tanggal" },
    { label: "Status" },
    { label: "Aksi", align: "right" },
  ],
};

const PER_PAGE = 5;

/* ============================================
   Shared Micro-Components
   ============================================ */
const SkeletonPulse = ({ className }) => (
  <div className={`animate-pulse rounded-lg bg-slate-700/40 ${className}`} />
);

const StatusBadge = ({ status }) => {
  const theme = STATUS_THEMES[status] || STATUS_THEMES.default;
  const Icon = theme.icon;

  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider"
      style={{
        background: theme.bg,
        border: theme.border,
        color: theme.color,
      }}
    >
      <Icon size={11} />
      {status || "Unknown"}
    </span>
  );
};

const EmptyState = ({ message, sub }) => (
  <tr>
    <td colSpan={100}>
      <div className="flex flex-col items-center justify-center py-14 text-center">
        <div
          className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4"
          style={{
            background: "rgba(100, 116, 139, 0.08)",
            border: "1px solid rgba(100, 116, 139, 0.15)",
          }}
        >
          <FiInbox className="text-2xl text-slate-600" />
        </div>
        <p className="text-sm font-bold text-slate-400 mb-1">{message}</p>
        <p className="text-xs text-slate-600 max-w-xs">{sub}</p>
      </div>
    </td>
  </tr>
);

const Pagination = ({ currentPage, totalPages, onPageChange }) => {
  if (totalPages <= 1) return null;

  return (
    <div
      className="flex items-center justify-between px-5 py-4"
      style={{ borderTop: "1px solid rgba(51, 65, 85, 0.2)" }}
    >
      <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
        Halaman {currentPage} dari {totalPages}
      </span>
      <div className="flex items-center gap-2">
        <PaginationBtn
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage <= 1}
        >
          <FiChevronLeft size={14} />
        </PaginationBtn>
        <PaginationBtn
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage >= totalPages}
        >
          <FiChevronRight size={14} />
        </PaginationBtn>
      </div>
    </div>
  );
};

const PaginationBtn = ({ children, onClick, disabled }) => {
  const base = {
    background: "rgba(51, 65, 85, 0.2)",
    border: "1px solid rgba(51, 65, 85, 0.2)",
    color: "#94a3b8",
  };
  const hover = {
    background: "rgba(0, 180, 216, 0.1)",
    border: "1px solid rgba(0, 180, 216, 0.2)",
    color: "#22d3ee",
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="p-2 rounded-lg transition-all disabled:opacity-30 disabled:cursor-not-allowed"
      style={base}
      onMouseEnter={(e) => !disabled && Object.assign(e.currentTarget.style, hover)}
      onMouseLeave={(e) => Object.assign(e.currentTarget.style, base)}
    >
      {children}
    </button>
  );
};

const ActionButton = ({ icon: Icon, label, onClick, color = "#22d3ee" }) => {
  const base = {
    background: `${color}14`,
    border: `1px solid ${color}26`,
    color: color,
  };
  const hover = {
    background: color,
    border: `1px solid ${color}`,
    color: "#ffffff",
  };

  return (
    <button
      onClick={onClick}
      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-all"
      style={base}
      onMouseEnter={(e) => Object.assign(e.currentTarget.style, hover)}
      onMouseLeave={(e) => Object.assign(e.currentTarget.style, base)}
    >
      <Icon size={12} />
      {label}
    </button>
  );
};

const formatRupiah = (val) =>
  new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(val);

/* ============================================
   Skeleton Row Generator
   ============================================ */
const SkeletonRows = ({ tab }) => (
  <>
    {Array.from({ length: PER_PAGE }).map((_, i) => (
      <tr key={`skel-${tab}-${i}`}>
        {HEADERS[tab].map((_, col) => (
          <td key={col} className="px-5 py-4">
            <SkeletonPulse
              className="h-4"
              style={{
                width: col === 0 ? "24px" : col === HEADERS[tab].length - 1 ? "80px" : `${50 + Math.random() * 40}%`,
                marginLeft: HEADERS[tab][col]?.align === "right" ? "auto" : undefined,
              }}
            />
          </td>
        ))}
      </tr>
    ))}
  </>
);

/* ============================================
   TAB A: Riwayat Pembayaran
   ============================================ */
const PaymentTab = ({ pppoeUserId, refreshData }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetch = async (p = 1) => {
    if (!pppoeUserId) return;
    setLoading(true);
    try {
      // const res = await PppoePaymentService.list({ pppoeUserId, page: p, perPage: PER_PAGE });
      // setData(res.data?.data || []);
      // setTotalPages(res.data?.last_page || Math.ceil((res.data?.total || 0) / PER_PAGE) || 1);
    } catch {
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetch(page); }, [pppoeUserId, page]); // eslint-disable-line
  useEffect(() => { if (refreshData) fetch(page); }, [refreshData]); // eslint-disable-line

  const startIdx = (page - 1) * PER_PAGE;

  return (
    <>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr style={{ background: "rgba(15, 23, 42, 0.7)", borderBottom: "1px solid rgba(51, 65, 85, 0.3)" }}>
              {HEADERS.payment.map((h, i) => (
                <th key={i} className="px-5 py-3.5 text-[10px] font-bold text-slate-400 uppercase tracking-widest" style={{ textAlign: h.align || "left" }}>
                  {h.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y" style={{ borderColor: "rgba(51, 65, 85, 0.15)" }}>
            {loading ? (
              <SkeletonRows tab="payment" />
            ) : data.length === 0 ? (
              <EmptyState message="Belum Ada Pembayaran" sub="Riwayat pembayaran akan muncul setelah pelanggan melakukan pembayaran pertama." />
            ) : (
              data.map((pay, idx) => (
                <tr
                  key={pay.id || idx}
                  className="transition-colors duration-150"
                  onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(0, 180, 216, 0.04)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                >
                  <td className="px-5 py-4 text-xs font-bold text-slate-500 font-mono">{startIdx + idx + 1}</td>
                  <td className="px-5 py-4 text-sm font-semibold text-gray-100">{pay.periode || pay.bulan || "-"}</td>
                  <td className="px-5 py-4 text-xs text-slate-400 font-medium">{pay.tanggal_bayar || pay.tanggal || "-"}</td>
                  <td className="px-5 py-4">
                    {pay.metode ? (
                      <span className="text-[10px] font-bold text-slate-400 px-2 py-0.5 rounded" style={{ background: "rgba(51, 65, 85, 0.2)", border: "1px solid rgba(51, 65, 85, 0.2)" }}>
                        {pay.metode}
                      </span>
                    ) : (
                      <span className="text-xs text-slate-600">—</span>
                    )}
                  </td>
                  <td className="px-5 py-4 text-sm font-bold text-slate-200 text-right">{pay.jumlah ? formatRupiah(pay.jumlah) : "-"}</td>
                  <td className="px-5 py-4"><StatusBadge status={pay.status} /></td>
                  <td className="px-5 py-4 text-right">
                    <ActionButton icon={FiFileText} label="Invoice" onClick={() => alert(`Invoice: ${pay.id || pay.periode}`)} />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {!loading && <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />}
    </>
  );
};

/* ============================================
   TAB B: Data Invoice
   ============================================ */
const InvoiceTab = ({ pppoeUserId, refreshData }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetch = async (p = 1) => {
    if (!pppoeUserId) return;
    setLoading(true);
    try {
      // const res = await PppoeInvoiceService.list({ pppoeUserId, page: p, perPage: PER_PAGE });
      // setData(res.data?.data || []);
      // setTotalPages(res.data?.last_page || Math.ceil((res.data?.total || 0) / PER_PAGE) || 1);
    } catch {
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetch(page); }, [pppoeUserId, page]); // eslint-disable-line
  useEffect(() => { if (refreshData) fetch(page); }, [refreshData]); // eslint-disable-line

  const startIdx = (page - 1) * PER_PAGE;

  return (
    <>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr style={{ background: "rgba(15, 23, 42, 0.7)", borderBottom: "1px solid rgba(51, 65, 85, 0.3)" }}>
              {HEADERS.invoice.map((h, i) => (
                <th key={i} className="px-5 py-3.5 text-[10px] font-bold text-slate-400 uppercase tracking-widest" style={{ textAlign: h.align || "left" }}>
                  {h.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y" style={{ borderColor: "rgba(51, 65, 85, 0.15)" }}>
            {loading ? (
              <SkeletonRows tab="invoice" />
            ) : data.length === 0 ? (
              <EmptyState message="Belum Ada Invoice" sub="Invoice akan otomatis dibuat setelah pembayaran dikonfirmasi." />
            ) : (
              data.map((inv, idx) => (
                <tr
                  key={inv.id || idx}
                  className="transition-colors duration-150"
                  onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(0, 180, 216, 0.04)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                >
                  <td className="px-5 py-4 text-xs font-bold text-slate-500 font-mono">{startIdx + idx + 1}</td>
                  <td className="px-5 py-4">
                    <span className="font-mono text-xs text-cyan-400/80 bg-cyan-500/8 px-2 py-0.5 rounded">
                      {inv.no_invoice || "-"}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-sm font-semibold text-gray-100">{inv.periode || "-"}</td>
                  <td className="px-5 py-4 text-sm font-bold text-slate-200 text-right">{inv.jumlah ? formatRupiah(inv.jumlah) : "-"}</td>
                  <td className="px-5 py-4"><StatusBadge status={inv.status} /></td>
                  <td className="px-5 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <ActionButton icon={FiEye} label="Lihat" onClick={() => alert(`Lihat: ${inv.no_invoice}`)} color="#22d3ee" />
                      <ActionButton icon={FiDownload} label="Unduh" onClick={() => alert(`Unduh: ${inv.no_invoice}`)} color="#34d399" />
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {!loading && <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />}
    </>
  );
};

/* ============================================
   TAB C: Tiket Gangguan
   ============================================ */
const TicketTab = ({ pppoeUserId, refreshData }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetch = async (p = 1) => {
    if (!pppoeUserId) return;
    setLoading(true);
    try {
      // const res = await PppoeTicketService.list({ pppoeUserId, page: p, perPage: PER_PAGE });
      // setData(res.data?.data || []);
      // setTotalPages(res.data?.last_page || Math.ceil((res.data?.total || 0) / PER_PAGE) || 1);
    } catch {
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetch(page); }, [pppoeUserId, page]); // eslint-disable-line
  useEffect(() => { if (refreshData) fetch(page); }, [refreshData]); // eslint-disable-line

  const startIdx = (page - 1) * PER_PAGE;

  return (
    <>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr style={{ background: "rgba(15, 23, 42, 0.7)", borderBottom: "1px solid rgba(51, 65, 85, 0.3)" }}>
              {HEADERS.ticket.map((h, i) => (
                <th key={i} className="px-5 py-3.5 text-[10px] font-bold text-slate-400 uppercase tracking-widest" style={{ textAlign: h.align || "left" }}>
                  {h.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y" style={{ borderColor: "rgba(51, 65, 85, 0.15)" }}>
            {loading ? (
              <SkeletonRows tab="ticket" />
            ) : data.length === 0 ? (
              <EmptyState message="Belum Ada Tiket" sub="Tiket gangguan akan muncul jika pelanggan melaporkan masalah koneksi." />
            ) : (
              data.map((tiket, idx) => (
                <tr
                  key={tiket.id || idx}
                  className="transition-colors duration-150"
                  onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(0, 180, 216, 0.04)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                >
                  <td className="px-5 py-4 text-xs font-bold text-slate-500 font-mono">{startIdx + idx + 1}</td>
                  <td className="px-5 py-4">
                    <span className="font-mono text-xs text-amber-400/80 bg-amber-500/8 px-2 py-0.5 rounded">
                      {tiket.no_tiket || "-"}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-sm text-slate-300 max-w-[200px] truncate" title={tiket.keluhan}>
                    {tiket.keluhan || "-"}
                  </td>
                  <td className="px-5 py-4 text-xs text-slate-400 font-medium">{tiket.tanggal || tiket.created_at || "-"}</td>
                  <td className="px-5 py-4"><StatusBadge status={tiket.status} /></td>
                  <td className="px-5 py-4 text-right">
                    <ActionButton icon={FiEye} label="Detail" onClick={() => alert(`Detail: ${tiket.no_tiket}`)} color="#fb923c" />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {!loading && <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />}
    </>
  );
};

/* ============================================
   KOMPONEN UTAMA: PaymentSection (Tabbed)
   ============================================ */
export const PaymentSection = ({ pppoeUserId, refreshData }) => {
  const [activeTab, setActiveTab] = useState("payment");

  const activeTabConfig = TABS.find((t) => t.key === activeTab);

  return (
    <article
      className="rounded-2xl overflow-hidden transition-all duration-300"
      style={CARD_STYLE}
      onMouseEnter={(e) => Object.assign(e.currentTarget.style, CARD_HOVER_STYLE)}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = "rgba(51, 65, 85, 0.3)";
        e.currentTarget.style.boxShadow = "none";
      }}
    >
      {/* ── Card Header ── */}
      <div
        className="px-5 pt-5 pb-0"
        style={{ borderBottom: "none" }}
      >
        <div className="flex items-center gap-3 mb-4">
          {activeTabConfig && (
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{
                background: activeTabConfig.bg,
                border: `1px solid ${activeTabConfig.borderActive}`,
                boxShadow: `0 4px 12px ${activeTabConfig.color}26`,
              }}
            >
              <activeTabConfig.icon className="text-lg" style={{ color: activeTabConfig.color }} />
            </div>
          )}
          <div className="min-w-0">
            <h3 className="font-bold text-gray-100 text-sm tracking-tight">
              {activeTabConfig?.label || "Detail"}
            </h3>
          </div>
        </div>

        {/* ── Tab Buttons ── */}
        <div className="flex gap-1 -mb-px">
          {TABS.map((tab) => {
            const isActive = activeTab === tab.key;
            const Icon = tab.icon;

            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className="flex items-center gap-2 px-4 py-3 text-xs font-bold uppercase tracking-wider transition-all relative"
                style={{
                  color: isActive ? tab.color : "#64748b",
                  borderBottom: isActive
                    ? `2px solid ${tab.color}`
                    : "2px solid transparent",
                  marginBottom: "-1px",
                }}
                onMouseEnter={(e) => {
                  if (!isActive) e.currentTarget.style.color = tab.color;
                }}
                onMouseLeave={(e) => {
                  if (!isActive) e.currentTarget.style.color = "#64748b";
                }}
              >
                <Icon size={14} />
                <span className="hidden sm:inline">{tab.label}</span>
                <span className="sm:hidden">
                  {tab.key === "payment" ? "Bayar" : tab.key === "invoice" ? "Invoice" : "Tiket"}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Divider ── */}
      <div style={{ borderBottom: "1px solid rgba(51, 65, 85, 0.25)" }} />

      {/* ── Tab Content ── */}
      {activeTab === "payment" && (
        <PaymentTab pppoeUserId={pppoeUserId} refreshData={refreshData} />
      )}
      {activeTab === "invoice" && (
        <InvoiceTab pppoeUserId={pppoeUserId} refreshData={refreshData} />
      )}
      {activeTab === "ticket" && (
        <TicketTab pppoeUserId={pppoeUserId} refreshData={refreshData} />
      )}
    </article>
  );
};